import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { invalidateLookup } from "@/lib/lookupCache";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ViewToggle from "@/components/ViewToggle";
import useViewMode from "@/lib/useViewMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Plus, ArrowUpRight, LayoutDashboard, FolderKanban, Gem, Table2, Rows3, Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import AdvancedTable from "@/components/table/AdvancedTable";
import ClientsDashboardTab from "@/components/Client/ClientsDashboardTab";
import ClientsProjectsSubTab from "@/components/Client/ClientsProjectsSubTab";
import ClientsBrandsSubTab from "@/components/Client/ClientsBrandsSubTab";
import ClientsInlineTable from "@/components/Client/ClientsInlineTable";
import MyRoleChip from "@/components/MyRoleChip";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Textarea } from "@/components/ui/textarea";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

export default function Clients() {
  const { activeWorkspaceId } = useAuth();
  const { modules } = useWorkspacePerms();
  const brandsOn = modules?.brands !== false;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("tab") || "clients";
  const setActiveTab = (t) => setParams({ tab: t }, { replace: true });

  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCodesOpen, setBulkCodesOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [inline, setInline] = useState(true);
  const [form, setForm] = useState({ name: "", client_code: "", contact_email: "", industry: "", sub_industry: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useViewMode("clients");
  const [industriesCatalog, setIndustriesCatalog] = useState({ industries: [], sub_industries: {} });
  const [cardQ, setCardQ] = useState("");   // Feb 2026 · quick search in card view
  // R54 · Workspace members for Owner column dropdown.
  const [wsMembers, setWsMembers] = useState([]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then((r) => setWsMembers((r.data || []).filter(m => m.status !== "removed")))
      .catch(() => setWsMembers([]));
  }, [activeWorkspaceId]);

  useEffect(() => {
    api.get(`/reference/industries`)
      .then(({ data }) => setIndustriesCatalog(data))
      .catch(() => {});
  }, []);

  const columns = [
    { key: "name", label: "Client", width: 280, sortable: true, filterType: "text", frozen: true,
      render: (c) => (
        <div className="min-w-0 flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center font-bold text-xs shrink-0">
            {c.name?.[0]?.toUpperCase()}
          </div>
          <span className="font-medium truncate">{c.name}</span>
          {c.my_role && <MyRoleChip role={c.my_role} perms={c.my_role_perms} entityKind="client" />}
        </div>
      ),
    },
    { key: "contact_email", label: "Email", width: 240, sortable: true, filterType: "text",
      render: (c) => <span className="text-xs text-muted-foreground truncate">{c.contact_email || "—"}</span>,
    },
    { key: "display_name", label: "Display name", width: 180, sortable: true, filterType: "text",
      render: (c) => <span className="text-xs text-muted-foreground truncate">{c.display_name || "—"}</span>,
    },
    { key: "project_count", label: "Projects", width: 100, sortable: true, align: "right", type: "number", filterType: "number-range" },
    { key: "credit_rating", label: "Credit rating", width: 120, sortable: true, filterType: "select",
      filterOptions: ["AAA","AA","A","BBB","BB","B","not_rated"].map((v) => ({ value: v, label: v })),
      render: (c) => c.credit_rating ? <span className="chip text-[10px]">{c.credit_rating}</span> : <span className="text-muted-foreground">—</span>,
    },
    { key: "csat_score", label: "CSAT", width: 80, sortable: true, align: "right", type: "number",
      render: (c) => c.csat_score != null ? <span className="text-xs">{Number(c.csat_score).toFixed(1)} ★</span> : <span className="text-muted-foreground">—</span>,
    },
    { key: "retention_risk", label: "Retention risk", width: 130, sortable: true, filterType: "select",
      filterOptions: ["low","medium","high","churning"].map((v) => ({ value: v, label: v })),
      render: (c) => {
        const t = c.retention_risk;
        const cls = t === "churning" ? "bg-red-500/15 text-red-700 dark:text-red-300"
          : t === "high" ? "bg-orange-500/15 text-orange-700 dark:text-orange-300"
          : t === "medium" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
          : t === "low" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "";
        return t ? <span className={`chip text-[10px] ${cls}`}>{t}</span> : <span className="text-muted-foreground">—</span>;
      },
    },
    { key: "renewal_date", label: "Renewal", width: 110, sortable: true, filterType: "date-range",
      render: (c) => c.renewal_date ? <span className="text-xs">{String(c.renewal_date).slice(0,10)}</span> : <span className="text-muted-foreground">—</span>,
    },
    // R54 · Account Owner column with inline change.
    { key: "owner_user_id", label: "Owner", width: 170, sortable: true, groupable: true,
      accessor: (c) => c.owner_name || c.owner_user_id || "",
      render: (c) => (
        <select
          value={c.owner_user_id || ""}
          onChange={async (e) => {
            e.stopPropagation();
            const v = e.target.value || null;
            try {
              const { data } = await api.patch(`/clients/${c.id}`, { owner_user_id: v });
              setClients((prev) => prev.map((x) => x.id === c.id ? { ...x, owner_user_id: v, owner_name: data?.owner_name || null } : x));
              toast.success("Owner updated");
            } catch (err) { toast.error(apiErrorMessage(err, "Failed to update owner")); }
          }}
          onClick={(e) => e.stopPropagation()}
          className="h-7 text-xs rounded-none border border-transparent hover:border-border bg-background px-1 w-full"
          data-testid={`client-owner-${c.id}`}
        >
          <option value="">Unassigned</option>
          {wsMembers.map((m) => (
            <option key={m.user_id} value={m.user_id}>{m.name || m.email || m.user_id}</option>
          ))}
        </select>
      ),
    },
    { key: "created_at", label: "Added", width: 130, sortable: true, align: "right", filterType: "date-range",
      render: (c) => <span className="text-xs text-muted-foreground">{fmtRelative(c.created_at)}</span>,
    },
  ];

  const load = async () => {
    if (!activeWorkspaceId) return;
    const { data } = await api.get(`/workspaces/${activeWorkspaceId}/clients`);
    setClients(data);
    setLoaded(true);
  };

  useEffect(() => { load(); }, [activeWorkspaceId]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        client_code: form.client_code.trim() || null,
        contact_email: form.contact_email.trim() || null,
        industry: form.industry || null,
        sub_industry: form.sub_industry || null,
        website: form.website.trim() || null,
      };
      await api.post(`/workspaces/${activeWorkspaceId}/clients`, payload);
      invalidateLookup("clients", activeWorkspaceId);
      setForm({ name: "", client_code: "", contact_email: "", industry: "", sub_industry: "", website: "" });
      setOpen(false);
      load();
      toast.success("Client created");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to create client"));
    } finally { setBusy(false); }
  };

  const bulkCreate = async () => {
    const names = bulkText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!names.length) { toast.error("Type at least one name"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/clients/bulk-create`, { names });
      invalidateLookup("clients", activeWorkspaceId);
      toast.success(`Created ${data.created_count}${data.skipped_count ? `, skipped ${data.skipped_count} duplicate(s)` : ""}`);
      setBulkOpen(false); setBulkText("");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Bulk create failed"));
    } finally { setBulkBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Clients"
        title="Your clients"
        density="dense"
        description="Health rollups, projects, brands and everything you need to steward each engagement."
        testid="clients-header"
        actions={
          <div className="flex items-center gap-2">
            {activeTab === "clients" && (
              <>
                <button
                  type="button"
                  onClick={() => setInline((v) => !v)}
                  className={`inline-flex items-center gap-1 h-9 border px-2 text-xs uppercase tracking-widest font-semibold ${inline ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary"}`}
                  data-testid="clients-inline-toggle"
                >
                  {inline ? <Rows3 className="w-3.5 h-3.5" /> : <Table2 className="w-3.5 h-3.5" />}
                  {inline ? "Card / List" : "Excel view"}
                </button>
                <ViewToggle mode={mode} onChange={setMode} testid="clients-view-toggle" />
                <Button variant="outline" className="rounded-none" onClick={() => setBulkOpen(true)} data-testid="clients-bulk-btn">
                  <Plus className="w-4 h-4 mr-1" /> Bulk add
                </Button>
                <Button variant="outline" className="rounded-none" onClick={() => setBulkCodesOpen(true)} data-testid="clients-bulk-codes-btn">
                  <Hash className="w-4 h-4 mr-1" /> Assign codes
                </Button>
              </>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none" data-testid="clients-new-btn"><Plus className="w-4 h-4 mr-2" />New client</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create client</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-4">
                  <div className="grid grid-cols-[1fr_140px] gap-3">
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Name</Label>
                      <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" />
                    </div>
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Client code</Label>
                      <Input value={form.client_code || ""} onChange={(e) => setForm({ ...form, client_code: e.target.value })} placeholder="ACM-001" data-testid="client-form-code" />
                    </div>
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-2 inline-block">Contact email</Label>
                    <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-form-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Industry</Label>
                      <SearchableSelect
                        value={form.industry}
                        onValueChange={(v) => setForm({ ...form, industry: v, sub_industry: "" })}
                        options={industriesCatalog.industries || []}
                        placeholder="— Select industry —"
                        clearable
                        data-testid="client-form-industry"
                        width="100%"
                      />
                    </div>
                    <div>
                      <Label className="label-eyebrow mb-2 inline-block">Sub-industry</Label>
                      <SearchableSelect
                        value={form.sub_industry}
                        onValueChange={(v) => setForm({ ...form, sub_industry: v })}
                        options={industriesCatalog.sub_industries?.[form.industry] || []}
                        placeholder={form.industry ? "— Select sub-industry —" : "Pick industry first"}
                        disabled={!form.industry}
                        clearable
                        data-testid="client-form-sub-industry"
                        width="100%"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="label-eyebrow mb-2 inline-block">Website (optional)</Label>
                    <Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="client-form-website" />
                  </div>
                  <Button type="submit" className="w-full rounded-none" disabled={busy} data-testid="client-form-submit">
                    {busy ? "Creating…" : "Create"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="rounded-none">
            <TabsTrigger value="dashboard" className="rounded-none" data-testid="clients-subtab-dashboard">
              <LayoutDashboard className="w-3.5 h-3.5 mr-1.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="clients" className="rounded-none" data-testid="clients-subtab-clients">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Clients{loaded ? ` · ${clients.length}` : ""}
            </TabsTrigger>
            <TabsTrigger value="projects" className="rounded-none" data-testid="clients-subtab-projects">
              <FolderKanban className="w-3.5 h-3.5 mr-1.5" /> Projects
            </TabsTrigger>
            {brandsOn && (
              <TabsTrigger value="brands" className="rounded-none" data-testid="clients-subtab-brands">
                <Gem className="w-3.5 h-3.5 mr-1.5" /> Brands
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="dashboard" className="pt-6">
            <ClientsDashboardTab />
          </TabsContent>

          <TabsContent value="clients" className="pt-6">
            {!loaded ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : clients.length === 0 ? (
              <EmptyState
                title="No clients yet"
                description="Create your first client to start organizing projects."
                icon={Users}
                testid="clients-empty"
                action={<Button onClick={() => setOpen(true)} className="rounded-none" data-testid="clients-empty-create-btn">Create first client</Button>}
              />
            ) : inline ? (
              <ClientsInlineTable
                rows={clients}
                onSaved={load}
                industries={industriesCatalog.industries || []}
                subIndustriesMap={industriesCatalog.sub_industries || {}}
              />
            ) : mode === "list" ? (
              <AdvancedTable
                tableKey="clients"
                title="Clients"
                rows={clients}
                columns={columns}
                loading={!loaded}
                emptyIcon={Users}
                emptyTitle="No clients yet"
                emptyDescription="Create your first client to start organizing projects."
                onRowClick={(c) => navigate(`/app/clients/${c.id}`)}
              />
            ) : (
              <>
                {/* Feb 2026 · Quick search over the card grid */}
                <div className="mb-3 border border-border bg-muted/30 p-2 flex items-center gap-2" data-testid="clients-card-search-panel">
                  <Users className="w-3.5 h-3.5" />
                  <Input value={cardQ} onChange={(e) => setCardQ(e.target.value)}
                          placeholder="Quick search name, industry, contact email…"
                          className="h-8 w-80 text-xs" data-testid="clients-card-q" />
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    {clients.filter((c) => {
                      if (!cardQ) return true;
                      const s = cardQ.toLowerCase();
                      return (c.name || "").toLowerCase().includes(s)
                        || (c.industry || "").toLowerCase().includes(s)
                        || (c.sub_industry || "").toLowerCase().includes(s)
                        || (c.contact_email || "").toLowerCase().includes(s)
                        || (c.display_name || "").toLowerCase().includes(s);
                    }).length} of {clients.length}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="clients-grid">
                  {clients.filter((c) => {
                    if (!cardQ) return true;
                    const s = cardQ.toLowerCase();
                    return (c.name || "").toLowerCase().includes(s)
                      || (c.industry || "").toLowerCase().includes(s)
                      || (c.sub_industry || "").toLowerCase().includes(s)
                      || (c.contact_email || "").toLowerCase().includes(s)
                      || (c.display_name || "").toLowerCase().includes(s);
                  }).map((c) => (
                  <Link
                    key={c.id}
                    to={`/app/clients/${c.id}`}
                    className="border border-border p-6 hover:border-primary/40 group transition"
                    data-testid={`client-card-${c.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 rounded-sm bg-secondary flex items-center justify-center font-bold">
                        {c.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex items-center gap-1">
                        {c.my_role && <MyRoleChip role={c.my_role} perms={c.my_role_perms} entityKind="client" />}
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                      </div>
                    </div>
                    <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "var(--font-heading)" }}>{c.name}</h3>
                    <div className="text-xs text-muted-foreground">{c.project_count} project{c.project_count === 1 ? "" : "s"} · added {fmtRelative(c.created_at)}</div>
                    {c.contact_email && <div className="text-xs text-muted-foreground mt-2 truncate">{c.contact_email}</div>}
                  </Link>
                ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="projects" className="pt-6">
            <ClientsProjectsSubTab />
          </TabsContent>

          <TabsContent value="brands" className="pt-6">
            <ClientsBrandsSubTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Iter 200a · Bulk create dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="clients-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk add clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Paste or type client names — one per line. Duplicates are
              skipped automatically. You can fill in industry / email / etc.
              afterwards using the Excel view.
            </p>
            <Textarea
              rows={10}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={"Acme Coffee\nContoso Ltd\nGlobex Corp"}
              className="rounded-none font-mono text-sm"
              data-testid="clients-bulk-input"
            />
            <div className="text-[11px] text-muted-foreground">
              {bulkText.split("\n").filter((s) => s.trim()).length} name(s)
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={bulkCreate} disabled={bulkBusy} data-testid="clients-bulk-submit">
              {bulkBusy ? "Creating…" : "Create clients"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <BulkAssignCodesDialog
        wsId={activeWorkspaceId}
        open={bulkCodesOpen}
        onClose={() => setBulkCodesOpen(false)}
        onDone={load}
      />
    </>
  );
}

function BulkAssignCodesDialog({ wsId, open, onClose, onDone }) {
  const [prefix, setPrefix] = useState("");
  const [pad, setPad] = useState(3);
  const [overwrite, setOverwrite] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (!open || !wsId) return;
    api.get(`/workspaces/${wsId}/settings/clients`)
      .then(({ data }) => { if (data?.client_code_prefix) setPrefix(data.client_code_prefix); })
      .catch(() => {});
    setPreview(null);
     
  }, [open, wsId]);

  const runPreview = async () => {
    if (!prefix.trim()) { toast.error("Enter a prefix"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/clients/bulk-assign-codes`, {
        prefix: prefix.trim(), pad: Number(pad), overwrite, dry_run: true,
      });
      setPreview(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/clients/bulk-assign-codes`, {
        prefix: prefix.trim(), pad: Number(pad), overwrite, dry_run: false,
      });
      toast.success(`Assigned ${data.updated} code${data.updated === 1 ? "" : "s"}`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Commit failed")); }
    finally { setCommitting(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="bulk-codes-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Hash className="w-4 h-4" /> Auto-assign client codes</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-muted-foreground">
            Generates sequential codes like <span className="font-mono">{prefix || "PREFIX"}-{"001"}</span>, <span className="font-mono">{prefix || "PREFIX"}-{"002"}</span>, … for every client without a code (or all clients when Overwrite is on).
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Prefix</Label>
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toUpperCase())} placeholder="ACM" data-testid="bulk-codes-prefix" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Zero-pad width</Label>
              <Input type="number" min={1} max={8} value={pad} onChange={(e) => setPad(e.target.value)} data-testid="bulk-codes-pad" />
            </div>
            <div className="flex items-end">
              <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="bulk-codes-overwrite" />
                Overwrite existing codes
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={runPreview} disabled={busy} data-testid="bulk-codes-preview">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              {busy ? "Previewing…" : "Preview"}
            </Button>
          </div>
          {preview && (
            <div className="border border-border" data-testid="bulk-codes-preview-panel">
              <div className="px-3 py-2 bg-muted/40 border-b border-border text-xs font-semibold">
                Preview · {preview.would_update} client{preview.would_update === 1 ? "" : "s"} will be updated · next seq starts at {preview.next_seq}
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border">
                      <th className="px-2 py-1.5">Client</th>
                      <th className="px-2 py-1.5">Previous code</th>
                      <th className="px-2 py-1.5">New code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.assignments || []).slice(0, 200).map((a) => (
                      <tr key={a.id} className="border-b border-border/50" data-testid={`bulk-codes-row-${a.id}`}>
                        <td className="px-2 py-1.5 truncate">{a.name}</td>
                        <td className="px-2 py-1.5 font-mono text-muted-foreground">{a.previous_code || "—"}</td>
                        <td className="px-2 py-1.5 font-mono text-emerald-700 font-semibold">{a.new_code}</td>
                      </tr>
                    ))}
                    {(preview.assignments || []).length > 200 && (
                      <tr><td colSpan={3} className="px-2 py-2 text-center text-muted-foreground">… and {preview.assignments.length - 200} more</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
            <Button className="rounded-none" onClick={commit} disabled={!preview || committing || preview.would_update === 0} data-testid="bulk-codes-commit">
              {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              {committing ? "Assigning…" : preview ? `Assign ${preview.would_update} codes` : "Preview first"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
